let sessionAnalyticsParam: string | null = null;

/**
 * Stores the analytics param from the current session (`core_session.session_param`).
 * Called when the current user is loaded or cleared.
 */
export function setSessionAnalyticsParam(
  value: string | null | undefined,
): void {
  sessionAnalyticsParam = value ?? null;
}

function parseAnalyticsValue(
  value: string | null | undefined,
): number[] | null {
  try {
    if (!value || value.length < 2) {
      return null;
    }

    // Move last 2 symbols to beginning and swap them
    const lastTwo = value.slice(-2);
    const rest = value.slice(0, -2);
    const swapped = lastTwo[1] + lastTwo[0] + rest;

    const decoded = atob(swapped);
    const parsed = JSON.parse(decoded) as unknown;

    if (Array.isArray(parsed)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parses the analytics value from the current session's `session_param`.
 * The value is transformed by: moving the last 2 symbols to the beginning and swapping them,
 * then decoding from Base64 to get an array like [1, 1, 1, 1].
 *
 * @returns The decoded array, or null if the session param doesn't exist or parsing fails.
 */
export function getAnalyticsCookieValue(): number[] | null {
  return parseAnalyticsValue(sessionAnalyticsParam);
}

const ACTIONS_TO_HIDE_ON_EDIT = new Set([
  "ADD_QUESTION",
  "ADD_LINK_CARD",
  "ADD_SECTION",
  "ADD_FILTER_PARAMETER",
  "DASHBOARD_HEADER_ACTION_DIVIDER",
]);

/**
 * Returns true if the + New app bar control should be hidden (all routes)
 * and dashboard edit creation actions should be hidden, based on the analytics
 * session param (when second index equals 0).
 */
export function shouldHideDashboardCreationFeatures(): boolean {
  const analytics = getAnalyticsCookieValue();
  return analytics != null && analytics.length > 1 && analytics[1] === 0;
}

/**
 * Returns true if the Edit dashboard button should be hidden on the dashboard page,
 * based on the analytics session param (when third index equals 0).
 */
export function shouldHideEditDashboardButton(): boolean {
  const analytics = getAnalyticsCookieValue();
  return analytics != null && analytics.length > 2 && analytics[2] === 0;
}

/**
 * Returns true if the Remove and trash button on dashboard cards should be hidden
 * on the dashboard edit page, based on the analytics session param (when fourth index equals 0).
 */
export function shouldHideRemoveAndTrashButton(): boolean {
  const analytics = getAnalyticsCookieValue();
  return analytics != null && analytics.length > 3 && analytics[3] === 0;
}

/**
 * Filters out ADD_QUESTION, ADD_LINK_CARD, ADD_SECTION, ADD_FILTER_PARAMETER,
 * and DASHBOARD_HEADER_ACTION_DIVIDER when analytics session param indicates to hide them.
 */
export function filterDashboardEditActions<T extends string>(
  actions: T[],
): T[] {
  if (!shouldHideDashboardCreationFeatures()) {
    return actions;
  }
  return actions.filter((action) => !ACTIONS_TO_HIDE_ON_EDIT.has(action));
}

/**
 * Filters out EDIT_DASHBOARD when analytics session param indicates to hide it
 * (when third index equals 0).
 */
export function filterDashboardViewActions<T extends string>(
  actions: T[],
): T[] {
  if (!shouldHideEditDashboardButton()) {
    return actions;
  }
  return actions.filter((action) => action !== "EDIT_DASHBOARD");
}
